import { getUnauthenticatedLandingPath, WELCOME_LOGIN_PATH } from '@/core/constants/appConfig';
import { logger } from '@/core/logger';
import { router, type Href } from 'expo-router';
import { Platform } from 'react-native';
import { useAppStore } from '@/core/stores/appStore';

/**
 * Generic navigation helper for logout/session-expire flows.
 *
 * - Web: Bloom Universal when `ENABLE_BLOOM_UNIVERSAL_LANDING`, else welcome
 * - Native: always `WELCOME_LOGIN_PATH` (unchanged)
 * - Clears the navigation stack so guest landing is top (no back into protected routes).
 * - Optionally preserves a redirect path for post-login return.
 */
export async function navigateToWelcomeAfterLogout(options?: { redirectPath?: string | null, showToast?: boolean }) {
    const basePath =
        Platform.OS === 'web' ? getUnauthenticatedLandingPath() : WELCOME_LOGIN_PATH;
    const redirectPath = options?.redirectPath ?? null;
    const showToast = options?.showToast ?? false;

    let target = basePath;
    if (redirectPath && redirectPath !== '/' && redirectPath !== basePath) {
        const hasQuery = target.includes('?');
        target = hasQuery ? `${target}&redirect=${encodeURIComponent(redirectPath)}` : `${target}?redirect=${encodeURIComponent(redirectPath)}`;
    }
    
    if (showToast) {
        useAppStore.getState().setLogoutToastVisible(true);
    }

    // Use a more gentle navigation approach to avoid full app reload
    // First, try to clear the stack, then navigate
    // Add a small delay to ensure root layout is mounted before navigation
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nav = router as any;
        
        // Clear navigation stack first to prevent back navigation to protected routes
        if (nav.canGoBack?.()) {
            // Use dismissAll to clear the entire stack
            nav.dismissAll?.();
            // Small delay to ensure dismissAll completes before replace
            await new Promise(resolve => setTimeout(resolve, 50));
        }
    } catch (error) {
        logger.debug('LogoutNavigation', 'dismissAll failed, continuing with replace only', error);
    }

    // Use replace after clearing stack - this should not cause a full reload
    // The delay above ensures dismissAll completes first and root layout is mounted
    try {
        router.replace(target as Href);
    } catch (navError) {
        logger.error('LogoutNavigation', 'Failed to navigate after logout', navError);
        // Fallback: try again after a longer delay
        setTimeout(() => {
            try {
                router.replace(target as Href);
            } catch (retryError) {
                logger.error('LogoutNavigation', 'Retry navigation also failed', retryError);
            }
        }, 200);
    }
}
